import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  createStyles,
  FormControl,
  FormControlLabel,
  Grid,
  makeStyles,
  RadioGroup,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';

import { useLocation } from 'react-router';
import {
  Button, FormSimpleAutoComplete, FormSwitch, Modal,
} from '../../../components';
import { selectCurrentSceneId, selectListScenes } from '../../../store/modules/scenes/selectors';
import RadioButton from '../../../components/button/RadioButton';
import { downloadAllMedia } from '../../../store/modules/articles/actions';
import { Option } from '../../../components/form-simple-autocomplete/FormSimpleAutoComplete';

type Props = {
  closeHandler: () => void;
  open: boolean;
};

export type DownloadAll = {
  isNumbering: boolean;
  sceneId: string | number;
} | null;

const useStyles = makeStyles(() => createStyles({
  buttonBlock: {
    margin: '20px 0 0',
    padding: '20px 0',
  },
}));


const DownloadAllMediaAction = ({ closeHandler, open }: Props) => {
  const classes = useStyles();
  const [downloadMethod, setDownloadMethod] = useState<'script' | 'all'>('all');
  const [isNumberingDownload, setIsNumberingDownload] = useState<boolean>(true);
  const scenesList = useSelector(selectListScenes);
  const [selectedScriptNumber, setSelectedScriptNumber] = useState<number>(1);
  const dispatch = useDispatch();
  const selectedScriptId = useSelector(selectCurrentSceneId);
  const { pathname } = useLocation();
  const [selectedOption, setSelectedOption] = useState<Option | undefined>(undefined);

  const scenes = useMemo(() => {
    if (scenesList.data && scenesList.data.items.length > 0) {
      return scenesList.data.items.map((item, i) => ({
        label: item.title,
        id: i + 1,
      }));
    }
    return [];
  }, [scenesList]);

  const initialFormikValues = useMemo(() => {
    const isNumbering = localStorage.getItem('isNumberingDownload');
    if (isNumbering !== null) {
      setIsNumberingDownload(JSON.parse(isNumbering));
      return { select_script: '', number_chronologically: JSON.parse(isNumbering) };
    }
    return { select_script: '', number_chronologically: true };
  }, []);

  const selectedSceneId = useMemo(() => {
    const scriptIndex = selectedScriptNumber - 1;
    if (scenesList.data && scenesList.data.items.length && ( (scenesList.data.items.length - 1 )>= scriptIndex )) {
      return scenesList.data.items[scriptIndex].id;
    }
    return '';
  }, [selectedScriptNumber, scenesList]);

  const onChangeScriptHandler = useCallback((option?: Option) => {
    if (option) {
      setSelectedScriptNumber(parseInt(option.id.toString(), 10));
    }
  }, []);

  const changeDownloadMethod = (e: any) => {
    setDownloadMethod(e.target.value);
  };

  const changeIsNumberingHandler = useCallback((val: boolean) => {
    localStorage.setItem('isNumberingDownload', String(val));
    setIsNumberingDownload(val);
  }, []);

  const downloadHandler = useCallback(() => {
    if (downloadMethod === 'all') {
      dispatch(downloadAllMedia(null));
    } else {
      dispatch(downloadAllMedia({ isNumbering: isNumberingDownload, sceneId: selectedSceneId }));
    }
    closeHandler();
  }, [selectedSceneId, isNumberingDownload, downloadMethod, dispatch, closeHandler]);

  useEffect(() => {
    if (selectedScriptId && scenesList.data && scenesList.data.items.length) {
      const findScriptIndex = scenesList.data.items.findIndex((item) => item.id === selectedScriptId);
      if (findScriptIndex !== -1) {
        setSelectedScriptNumber(findScriptIndex + 1);
        setSelectedOption(scenes[findScriptIndex]);
      } else {
        setSelectedScriptNumber(1);
        setSelectedOption(scenes[0]);
      }
    }
  }, [pathname, scenesList, scenes, selectedScriptId]);

  return (
    <Formik initialValues={initialFormikValues} onSubmit={() => {}}>
      <Modal
        open={open}
        onCloseHandler={closeHandler}
        title="Download All"
        hideCancel
      >
        <div>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <RadioGroup aria-label="scripts" name="scripts" value={downloadMethod} onChange={changeDownloadMethod}>
                  <FormControlLabel value="all" control={<RadioButton />} label="Download All" />
                  <FormControlLabel value="script" control={<RadioButton />} label="Download based on script" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <>
              {
                (scenes)
                && (
                  <Grid item xs={12}>
                    <FormSimpleAutoComplete
                      name="select_script"
                      label="Script"
                      options={scenes}
                      callback={onChangeScriptHandler}
                      disabled={scenesList.loading || downloadMethod === 'all'}
                      selectedOption={selectedOption}
                    />
                  </Grid>
                )
              }
            </>
            <Grid item xs={12}>
              <FormSwitch
                label="Number chronologically"
                name="number_chronologically"
                disabled={downloadMethod === 'all'}
                checked={isNumberingDownload}
                onChangeCallback={changeIsNumberingHandler}
              />
            </Grid>
          </Grid>
          <Grid container alignItems="center" justify="flex-end" className={classes.buttonBlock}>
            <Button styleButton="orange" onClick={downloadHandler}>
              Download
            </Button>
          </Grid>
        </div>
      </Modal>
    </Formik>
  );
};

export default DownloadAllMediaAction;
